import { Injectable } from '@angular/core';

import * as _ from 'lodash';

import { StateService } from '@uirouter/core';
import { Schedule } from 'src/app/api/services/schedule';
import { SCHEDULE_ACCESS } from 'src/app/auth/types/access-level';
import { ActionsMenu } from 'src/app/components/common-actions/common-actions.component';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { BaseEditableComponent } from 'src/app/shared/components/base-editable/base-editable.component';
import {
  AccessControlList,
  ShareWithUsersModalComponent,
} from 'src/app/components/share-with-users-modal/share-with-users-modal.component';
import { ScheduleService } from './schedule.service';

@Injectable({
  providedIn: 'root',
})
export class ScheduleActionsService {
  constructor(
    private modalService: ModalService,
    private featuresService: FeaturesService,
    private scheduleService: ScheduleService,
    private trackerService: TrackerService,
    private stateService: StateService,
  ) {}

  getActions(component: BaseEditableComponent<any>): ActionsMenu {
    return {
      cancelAction: {
        id: 'cancelButton',
        label: 'Cancel',
        action: () => {
          component.bypassUnsavedChanges();
          this.stateService.go('apps.schedules.list');
        },
      },
      moreOptions: [
        {
          id: 'shareScheduleButton',
          label: 'Share with others',
          icon: 'add-user',
          upgradeIcon: () =>
            this.featuresService.isFeatureAvailable(
              FeaturesService.FEATURE_GRANULAR_USER_PERMISSIONS,
            )
              ? undefined
              : 'requires-upgrade',
          action: () => this.shareSchedule(),
          requireAccess: SCHEDULE_ACCESS.FULL_ACCESS,
          entityId: this.scheduleService.schedule?.id,
        },
      ],
    };
  }

  showShareDialog(accessControlList: Promise<AccessControlList>) {
    return this.modalService.showMediumDialog(
      ShareWithUsersModalComponent,
      {
        accessControlList,
        accessLevels: ['Playlist Manager', 'Full Access'],
        defaultAccessLevel: 'Playlist Manager',
        saveAccessControlList: async (
          updatedAccessControlList: AccessControlList,
          comment?: string,
        ) => {
          if (_.isEqual(await accessControlList, updatedAccessControlList)) {
            return;
          }

          const response = await this.scheduleService.updateAccessControlList(
            updatedAccessControlList as NonNullable<
              Schedule['accessControlList']
            >,
            comment,
          );
          this.scheduleService.schedule.accessControlList =
            response.item?.accessControlList ?? updatedAccessControlList;
          this.trackerService.scheduleEvent(
            'Schedule Access Changed',
            this.scheduleService.schedule.scheduleId,
            this.scheduleService.schedule.scheduleName,
            {
              sharedUsers: Object.values(updatedAccessControlList).filter(
                Boolean,
              ).length,
            },
          );
        },
      },
      { width: '640px', maxWidth: 'calc(100vw - 32px)' },
    );
  }

  shareSchedule(): Promise<void> {
    return this.featuresService
      .confirmShareWithOthersAvailable()
      .then(() => {
        const currentAccessControlList =
          this.scheduleService.getAccessControlList();

        return this.showShareDialog(currentAccessControlList);
      })
      .then(() => {})
      .catch(() => {});
  }
}
